import React, { useState, useEffect } from 'react'
import api from '../api/client'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { User, FileText, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'
import { useLocale } from '../contexts/LocaleContext'

interface ApplicationFormData {
  motivation: string
}

const CompleteVolunteerApplicationPage: React.FC = () => {
  console.log('CompleteVolunteerApplicationPage rendering')
  const { t } = useLocale()
  const { user, profile, volunteerRequest, profileLoading, refreshProfile } = useAuth()
  const navigate = useNavigate()
  
  console.log('CompleteVolunteerApplicationPage state:', { 
    user: !!user, 
    profile: !!profile, 
    volunteerRequest: !!volunteerRequest,
    profileCompleted: profile?.profile_completed,
    profileRole: profile?.role
  })

  // Add debug for redirect conditions
  useEffect(() => {
    console.log('CompleteVolunteerApplicationPage useEffect - checking redirect conditions')
    console.log('User:', !!user)
    console.log('Profile:', !!profile)
    console.log('Profile completed:', profile?.profile_completed)
    console.log('Profile role:', profile?.role)
    console.log('Volunteer request:', !!volunteerRequest)
    console.log('Volunteer request status:', volunteerRequest?.status)
  }, [user, profile, volunteerRequest])
  const [formData, setFormData] = useState<ApplicationFormData>({
    motivation: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    // Redirect if user is not logged in
    if (!user) {
      navigate('/login')
      return
    }

    // Redirect if profile exists but is not completed
    if (profile && !profile.profile_completed) {
      navigate('/complete-profile')
      return
    }

    // Redirect if user already has a volunteer application
    if (volunteerRequest) {
      if (volunteerRequest.status === 'pending') {
        navigate('/pending-approval')
      } else if (volunteerRequest.status === 'approved') {
        // If approved but profile role is still 'user', refresh profile and redirect
        if (profile?.role === 'user') {
          console.log('Volunteer request approved but profile role not updated, refreshing profile...')
          refreshProfile().then(() => {
            navigate('/dashboard')
          })
          return
        }
        navigate('/dashboard')
      }
    }

    // Redirect if user is already a volunteer
    if (profile?.role === 'volunteer' || profile?.role === 'admin') {
      navigate('/dashboard')
    }
  }, [user, profile, volunteerRequest, navigate, refreshProfile])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')

    // Optional motivation field with minimal character requirement
    if (formData.motivation.length > 0 && formData.motivation.length < 10) {
      setError('Please provide at least 10 characters for your motivation, or leave it empty')
      setLoading(false)
      return
    }

    try {
      const request = await api.volunteerRequests.create({
        email: user!.email!,
        first_name: profile?.first_name ?? undefined,
        last_name: profile?.last_name ?? undefined,
        phone_number: profile?.phone_number ?? undefined,
        motivation: formData.motivation
      })

      const autoApproved = request.status === 'approved'

      if (!autoApproved) {
        try {
          await api.notifications.send('new_application', {
            volunteerEmail: user!.email!,
            volunteerFirstName: profile?.first_name || '',
            volunteerLastName: profile?.last_name || '',
            motivation: formData.motivation,
            appliedAt: new Date().toISOString()
          })
        } catch (emailError) {
          console.warn('Admin notification failed (optional):', emailError)
        }
      }

      await refreshProfile()

      setSuccess(autoApproved
        ? 'Your volunteer application has been approved automatically. Welcome to the team!'
        : 'Your volunteer application has been submitted successfully! You will be notified via email once it has been reviewed.')
      setTimeout(() => navigate(autoApproved ? '/dashboard' : '/pending-approval'), 2000)
    } catch (error: any) {
      console.error('Error submitting volunteer application:', error)
      setError(error.message || 'Failed to submit volunteer application')
    } finally {
      setLoading(false)
    }
  }

  if (!user) {
    console.log('CompleteVolunteerApplicationPage: showing loading - no user')
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  // If profile is still loading, show a loading state but allow form to render
  if (profileLoading) {
    console.log('CompleteVolunteerApplicationPage: profile still loading')
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-600">Profiel laden...</p>
          <p className="mt-2 text-xs text-gray-500">Als dit te lang duurt, probeer de pagina te vernieuwen</p>
        </div>
      </div>
    )
  }

  console.log('CompleteVolunteerApplicationPage: about to render form')

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
            <User className="h-6 w-6 text-blue-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('volunteerApplication.title')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Welkom {profile?.first_name || user?.email}! Vertel ons waarom je vrijwilliger wilt worden.
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="rounded-md bg-green-50 p-4">
            <div className="flex">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <p className="text-sm text-green-800">{success}</p>
              </div>
            </div>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>

          <div>
            <label htmlFor="motivation" className="block text-sm font-medium text-gray-700">
              {t('volunteerApplication.motivation')} <span className="text-gray-500">({t('profile.optional')})</span>
            </label>
            <div className="mt-1 relative">
              <FileText className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <textarea
                id="motivation"
                name="motivation"
                rows={4}
                value={formData.motivation}
                onChange={handleInputChange}
                className="pl-10 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 resize-none"
                placeholder={t('volunteerApplication.motivationPlaceholder')}
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {formData.motivation.length} karakters {formData.motivation.length > 0 ? '(minimum 10 karakters)' : '(optioneel)'}
            </p>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                {loading ? t('volunteerApplication.submitting') : t('volunteerApplication.submitApplication')}
            </button>
          </div>

          
        </form>
      </div>
    </div>
  )
}

export default CompleteVolunteerApplicationPage


